/**
 * Static board metadata: the 8 numbered boards and the labels we know
 * for the 4×4=16 cells of each. Cell glosses come from the codebook
 * playtest selection — some cells are blank for now and can be filled
 * in once their official gloss is published.
 *
 * Where a cell appears in multiple codes with overlapping meanings
 * (e.g. 2A1 = "Dream / Think", 2B4 = "Help / Support / Speak / Complain"),
 * we keep the slash-joined union.
 */

import type { BoardMeta, BoardCellMeta, Column, Row } from "../game/types";

const COLS: Column[] = ["A", "B", "C", "D"];

function blankCells(boardId: number): BoardCellMeta[] {
  // Row-first iteration so the array matches the CSS grid's row-major auto-flow:
  // index 0 = A1 (top-left), 1 = B1, 2 = C1, 3 = D1, 4 = A2, … 15 = D4 (bottom-right).
  const cells: BoardCellMeta[] = [];
  for (let row = 1; row <= 4; row++) {
    for (const col of COLS) {
      cells.push({ ref: { board: boardId, col, row: row as Row }, gloss: {} });
    }
  }
  return cells;
}

function setCell(
  cells: BoardCellMeta[],
  key: `${Column}${Row}`,
  gloss: Partial<Record<"en" | "it" | "de" | "fr", string>>,
  opts?: { dualImage?: boolean },
) {
  const col = key[0] as Column;
  const row = Number(key[1]) as Row;
  const cell = cells.find((c) => c.ref.col === col && c.ref.row === row);
  if (!cell) throw new Error(`No cell ${key}`);
  cell.gloss = { ...cell.gloss, ...gloss };
  if (opts?.dualImage) cell.dualImage = true;
}

/* ---------- Board 1 — Persons ---------- */
const b1 = blankCells(1);
setCell(b1, "A1", { en: "I / Myself", it: "Io", de: "Ich", fr: "Je / Moi" });
setCell(b1, "A2", { en: "Child / Son", it: "Figlio", de: "Kind", fr: "Enfant" });
setCell(b1, "A3", { en: "Friends", it: "Amici", de: "Freunde", fr: "Amis" });
setCell(b1, "A4", { en: "Person", it: "Persona", de: "Person", fr: "Personne" });
setCell(b1, "B2", { en: "Parent / Grandparent", it: "Genitore / Nonno", de: "Elternteil / Großelternteil", fr: "Parent / Grand-parent" });
setCell(b1, "B3", { en: "Partner / Wife", it: "Coniuge / Moglie", de: "Partner / Ehefrau", fr: "Conjoint / Femme" });
setCell(b1, "B4", { en: "People / Community", it: "Popolo / Comunità", de: "Volk / Gemeinschaft", fr: "Peuple / Communauté" });
setCell(b1, "C1", { en: "You", it: "Tu / Voi", de: "Du / Ihr", fr: "Tu / Vous" });
setCell(b1, "C2", { en: "Sibling", it: "Fratello / Sorella", de: "Geschwister", fr: "Frère / Sœur" });
setCell(b1, "C3", { en: "Young", it: "Giovane", de: "Jung", fr: "Jeune" });
setCell(b1, "C4", { en: "Leader / Public figure", it: "Leader / Personaggio pubblico", de: "Anführer / Persönlichkeit", fr: "Leader / Personnalité" });
setCell(b1, "D3", { en: "Foreigner", it: "Straniero", de: "Ausländer", fr: "Étranger" });
setCell(b1, "D4", { en: "Run", it: "Correre", de: "Laufen", fr: "Courir" });

/* ---------- Board 2 — Actions ---------- */
const b2 = blankCells(2);
setCell(b2, "A1", { en: "Dream / Think", it: "Sognare / Pensare", de: "Träumen / Denken", fr: "Rêver / Penser" });
setCell(b2, "A2", { en: "Have / Decide", it: "Avere / Decidere", de: "Haben / Entscheiden", fr: "Avoir / Décider" });
setCell(b2, "B1", { en: "Eat", it: "Mangiare", de: "Essen", fr: "Manger" });
setCell(b2, "B2", { en: "Lock / Close", it: "Chiudere a chiave", de: "Schließen / Abschließen", fr: "Fermer à clé" });
setCell(b2, "B3", { en: "Search", it: "Cercare", de: "Suchen", fr: "Chercher" });
setCell(b2, "B4", { en: "Help / Support / Speak / Complain", it: "Aiutare / Sostenere / Parlare / Lamentarsi", de: "Helfen / Unterstützen / Sprechen / Klagen", fr: "Aider / Soutenir / Parler / Se plaindre" });
setCell(b2, "C2", { en: "Drive", it: "Guidare", de: "Fahren", fr: "Conduire" });
setCell(b2, "C3", { en: "Give / Offer", it: "Dare / Offrire", de: "Geben / Schenken", fr: "Donner / Offrir" });
setCell(b2, "C4", { en: "Grow / Improve / Goals", it: "Crescere / Migliorare / Obiettivi", de: "Wachsen / Verbessern / Ziele", fr: "Grandir / Améliorer / Objectifs" });
setCell(b2, "D1", { en: "Dream", it: "Sognare", de: "Träumen", fr: "Rêver" });
setCell(b2, "D2", { en: "Sleep", it: "Dormire", de: "Schlafen", fr: "Dormir" });
setCell(b2, "D3", { en: "Wish", it: "Desiderare", de: "Wünschen", fr: "Souhaiter" });

/* ---------- Board 3 — Objects + Actions ---------- */
const b3 = blankCells(3);
setCell(b3, "A2", { en: "Hit / Strike", it: "Colpire", de: "Schlagen", fr: "Frapper" });
setCell(b3, "A3", { en: "War / Conflict", it: "Guerra", de: "Krieg", fr: "Guerre" });
setCell(b3, "A4", { en: "Play", it: "Giocare", de: "Spielen", fr: "Jouer" });
setCell(b3, "B1", { en: "Art / Science (compound base)", it: "Arte / Scienza (composto)", de: "Kunst / Wissenschaft (Komp.)", fr: "Art / Science (composé)" });
setCell(b3, "B3", { en: "Say / Speak", it: "Dire / Parlare", de: "Sagen / Sprechen", fr: "Dire / Parler" });
setCell(b3, "B4", { en: "Yes / Agree", it: "Sì / D'accordo", de: "Ja / Einverstanden", fr: "Oui / D'accord" });
setCell(b3, "C2", { en: "Can / Be able to", it: "Potere", de: "Können", fr: "Pouvoir" });
setCell(b3, "C4", { en: "Search / Work / Job", it: "Cercare / Lavoro", de: "Suchen / Arbeit", fr: "Chercher / Travail" });
setCell(b3, "D1", { en: "Change / Transform / Become", it: "Cambiare / Diventare", de: "Verändern / Werden", fr: "Changer / Devenir" });
setCell(b3, "D2", { en: "Ask / Request", it: "Chiedere", de: "Bitten / Fragen", fr: "Demander" });
setCell(b3, "D3", { en: "Feel emotion", it: "Provare emozione", de: "Empfinden", fr: "Ressentir" });
setCell(b3, "D4", { en: "Build / Make", it: "Costruire", de: "Bauen / Machen", fr: "Construire" });

/* ---------- Board 4 — Objects ---------- */
const b4 = blankCells(4);
setCell(b4, "A1", { en: "Food", it: "Cibo", de: "Essen / Nahrung", fr: "Nourriture" });
setCell(b4, "A2", { en: "Money / Wealth", it: "Soldi", de: "Geld", fr: "Argent" });
setCell(b4, "A3", { en: "Afraid (X = Brave)", it: "Spaventato (X = Coraggioso)", de: "Ängstlich (X = mutig)", fr: "Effrayé (X = courageux)" }, { dualImage: true });
setCell(b4, "A4", { en: "Darkness", it: "Buio", de: "Dunkelheit", fr: "Obscurité" });
setCell(b4, "B1", { en: "Phone / Technology", it: "Telefono / Tecnologia", de: "Telefon / Technologie", fr: "Téléphone / Technologie" });
setCell(b4, "B2", { en: "Animal(s)", it: "Animale", de: "Tier", fr: "Animal" });
setCell(b4, "B3", { en: "Eye / Sight", it: "Occhio", de: "Auge", fr: "Œil" });
setCell(b4, "B4", { en: "Warm / Hot (X = Cold)", it: "Caldo (X = Freddo)", de: "Warm (X = kalt)", fr: "Chaud (X = froid)" }, { dualImage: true });
setCell(b4, "C4", { en: "Clothes", it: "Vestiti", de: "Kleidung", fr: "Vêtements" });
setCell(b4, "D2", { en: "Heart / Body", it: "Cuore / Corpo", de: "Herz / Körper", fr: "Cœur / Corps" });
setCell(b4, "D4", { en: "Car / Transport", it: "Auto / Trasporto", de: "Auto / Transport", fr: "Voiture / Transport" });

/* ---------- Board 5 — Times ---------- */
const b5 = blankCells(5);
setCell(b5, "A1", { en: "Time", it: "Tempo", de: "Zeit", fr: "Temps" });
setCell(b5, "A2", { en: "Week / Weekly / Period", it: "Settimana / Settimanale", de: "Woche / Wöchentlich", fr: "Semaine / Hebdomadaire" });
setCell(b5, "A3", { en: "Today", it: "Oggi", de: "Heute", fr: "Aujourd'hui" });
setCell(b5, "A4", { en: "Before / Early", it: "Prima / Presto", de: "Vor / Früh", fr: "Avant / Tôt" });
setCell(b5, "B1", { en: "Night", it: "Notte", de: "Nacht", fr: "Nuit" });
setCell(b5, "B3", { en: "Tomorrow", it: "Domani", de: "Morgen", fr: "Demain" });
setCell(b5, "B4", { en: "After / Later", it: "Dopo", de: "Nach / Später", fr: "Après / Plus tard" });
setCell(b5, "C2", { en: "Yearly / Per year", it: "Ogni anno", de: "Jährlich", fr: "Chaque année" });
setCell(b5, "C4", { en: "Often", it: "Spesso", de: "Oft", fr: "Souvent" });
setCell(b5, "D1", { en: "Winter / Season", it: "Inverno / Stagione", de: "Winter / Jahreszeit", fr: "Hiver / Saison" });
setCell(b5, "D4", { en: "Always (X = Never)", it: "Sempre (X = Mai)", de: "Immer (X = nie)", fr: "Toujours (X = jamais)" }, { dualImage: true });

/* ---------- Board 6 — Places ---------- */
const b6 = blankCells(6);
setCell(b6, "A1", { en: "Forest", it: "Foresta", de: "Wald", fr: "Forêt" });
setCell(b6, "A2", { en: "Path / Road", it: "Strada", de: "Weg / Straße", fr: "Chemin / Route" });
setCell(b6, "A3", { en: "World", it: "Mondo", de: "Welt", fr: "Monde" });
setCell(b6, "B1", { en: "Home", it: "Casa", de: "Haus / Heim", fr: "Maison" });
setCell(b6, "B2", { en: "Mountain", it: "Montagna", de: "Berg / Gebirge", fr: "Montagne" });
setCell(b6, "C1", { en: "City", it: "Città", de: "Stadt", fr: "Ville" });
setCell(b6, "C2", { en: "Space / Universe", it: "Spazio / Universo", de: "Weltraum / Universum", fr: "Espace / Univers" });
setCell(b6, "D1", { en: "School", it: "Scuola", de: "Schule", fr: "École" });
setCell(b6, "D2", { en: "Sea / Beach", it: "Mare / Spiaggia", de: "Meer / Strand", fr: "Mer / Plage" });
setCell(b6, "D3", { en: "Place / Location", it: "Luogo", de: "Ort", fr: "Lieu / Endroit" });

/* ---------- Board 7 — Adjectives ---------- */
const b7 = blankCells(7);
setCell(b7, "A1", { en: "Happy / Positive", it: "Felice", de: "Glücklich", fr: "Heureux" });
setCell(b7, "A2", { en: "Cold / Cool", it: "Freddo", de: "Kalt", fr: "Froid" });
setCell(b7, "A3", { en: "Latest / Last", it: "Ultimo", de: "Neueste / Letzte", fr: "Dernier" });
setCell(b7, "A4", { en: "Different", it: "Diverso", de: "Anders", fr: "Différent" });
setCell(b7, "B2", { en: "Easy / Peaceful", it: "Facile / Tranquillo", de: "Leicht / Friedlich", fr: "Facile / Paisible" });
setCell(b7, "B4", { en: "Big / Great", it: "Grande", de: "Groß", fr: "Grand" });
setCell(b7, "C1", { en: "Worried / Stressed", it: "Preoccupato", de: "Besorgt", fr: "Inquiet" });
setCell(b7, "C2", { en: "Ugly", it: "Brutto", de: "Hässlich", fr: "Laid" });
setCell(b7, "C3", { en: "Whole / Complete", it: "Intero", de: "Ganz / Vollständig", fr: "Entier" });
setCell(b7, "C4", { en: "Broken / Damaged", it: "Spezzato / Rotto", de: "Gebrochen", fr: "Brisé" });
setCell(b7, "D1", { en: "Adult (single) / Children (plural)", it: "Adulto / Bambini", de: "Erwachsen / Kinder", fr: "Adulte / Enfants" });
setCell(b7, "D2", { en: "Fat / Plump", it: "Grasso", de: "Dick", fr: "Gros" });
setCell(b7, "D3", { en: "Good / Approved", it: "Buono", de: "Gut", fr: "Bon" });
setCell(b7, "D4", { en: "Long (X = Short)", it: "Lungo (X = Corto)", de: "Lang (X = kurz)", fr: "Long (X = court)" }, { dualImage: true });

/* ---------- Board 8 — Conjunctions + Prepositions + Adverbs ---------- */
const b8 = blankCells(8);
setCell(b8, "A2", { en: "But / However", it: "Ma", de: "Aber / Jedoch", fr: "Mais" });
setCell(b8, "A3", { en: "To / Towards", it: "Verso / A", de: "Zu / Nach", fr: "Vers / À" });
setCell(b8, "A4", { en: "Almost", it: "Quasi", de: "Fast", fr: "Presque" });
setCell(b8, "B3", { en: "Through / Across", it: "Attraverso", de: "Durch / Über", fr: "À travers" });
setCell(b8, "B4", { en: "Which / What kind", it: "Quale / Che tipo", de: "Welche(r) / Was für", fr: "Lequel / Quel" });
setCell(b8, "C1", { en: "More / Greater", it: "Più", de: "Mehr / Größer", fr: "Plus" });
setCell(b8, "D1", { en: "Equals", it: "Uguale", de: "Gleich", fr: "Égal" });
setCell(b8, "D2", { en: "About / Of / Regarding", it: "Di / Riguardo a", de: "Über / Von", fr: "À propos de" });

/* ------------------------------ Export ------------------------------ */

export const BOARDS: BoardMeta[] = [
  { id: 1, theme: { en: "Persons", it: "Persone", de: "Personen", fr: "Personnes" }, cells: b1 },
  { id: 2, theme: { en: "Actions", it: "Azioni", de: "Aktionen", fr: "Actions" }, cells: b2 },
  { id: 3, theme: { en: "Objects + Actions", it: "Oggetti + Azioni", de: "Objekte + Aktionen", fr: "Objets + Actions" }, cells: b3 },
  { id: 4, theme: { en: "Objects", it: "Oggetti", de: "Objekte", fr: "Objets" }, cells: b4 },
  { id: 5, theme: { en: "Times", it: "Tempi", de: "Zeiten", fr: "Temps" }, cells: b5 },
  { id: 6, theme: { en: "Places", it: "Luoghi", de: "Orte", fr: "Lieux" }, cells: b6 },
  { id: 7, theme: { en: "Adjectives", it: "Aggettivi", de: "Adjektive", fr: "Adjectifs" }, cells: b7 },
  { id: 8, theme: { en: "Conj. + Prep. + Adv.", it: "Cong. + Prep. + Avv.", de: "Konj. + Präp. + Adv.", fr: "Conj. + Prép. + Adv." }, cells: b8 },
];

export function getCell(board: number, col: string, row: number): BoardCellMeta | undefined {
  return BOARDS[board - 1]?.cells.find((c) => c.ref.col === col && c.ref.row === row);
}
